"use client";

import React from "react";
import styles from "./services-status.module.scss";
import { HealthResponse, ServiceStatus, UnhealthyService } from "@/types/health.types";
import ServiceRowBadge from "../components/service-row-badge/service-row-badge";
import CountBadge from "../components/count-badge/count-badge";
import { formatLastBeat, formatSecondsSinceLastBeat } from "../utils/format-utils";
import { createScrollHandler } from "../utils/scroll-utils";

interface ServicesStatusProps {
  healthData: HealthResponse;
}

const ServicesStatus: React.FC<ServicesStatusProps> = ({ healthData }) => {
  const scrollToServicesContent = createScrollHandler('services-content');
  const scrollToUnhealthyServices = createScrollHandler('unhealthy-services');

  const renderServiceRow = (service: ServiceStatus, isHealthy: boolean) => (
    <ServiceRowBadge
      key={`${service.service}-${service.chainId}`}
      isHealthy={isHealthy}
      serviceName={service.service}
      chainId={service.chainId}
      metrics={[
        {
          label: "Last Beat",
          value: formatLastBeat(service.lastBeat)
        }
      ]}
    />
  );

  const renderUnhealthyServiceRow = (service: UnhealthyService) => (
    <ServiceRowBadge
      key={service.service}
      isHealthy={false}
      serviceName={service.service}
      chainId={null}
      metrics={[
        {
          label: "Last Beat",
          value: formatSecondsSinceLastBeat(service.secondsSinceLastBeat)
        }
      ]}
    />
  );

  return (
    <div id="services-status" className={styles.card}>
      <div className={styles.header}>
        <h3>Services Status</h3>
        <div className={styles.summary}>
          {healthData.services.healthy.length > 0 && (
            <CountBadge
              count={healthData.services.healthy.length}
              label="Healthy"
              isHealthy={true}
              onClick={scrollToServicesContent}
            />
          )}
          {healthData.services.unhealthy.length > 0 && (
            <CountBadge
              count={healthData.services.unhealthy.length}
              label="Unhealthy"
              isHealthy={false}
              onClick={scrollToUnhealthyServices}
            />
          )}
        </div>
      </div>

      <div id="services-content" className={styles.content}>
        {healthData.services.healthy.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.badgeSmallHealthy}>
                Healthy
              </div>
              <span>Services ({healthData.services.healthy.length})</span>
            </div>
            <div className={styles.servicesList}>
              {healthData.services.healthy
                .sort((a, b) => a.service.localeCompare(b.service))
                .map(service =>
                  renderServiceRow(service, true)
                )}
            </div>
          </div>
        )}

        {healthData.services.unhealthy.length > 0 && (
          <div id="unhealthy-services" className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.badgeSmallUnhealthy}>
                Unhealthy
              </div>
              <span>Services ({healthData.services.unhealthy.length})</span>
            </div>
            <div className={styles.servicesList}>
              {healthData.services.unhealthy
                .sort((a, b) => a.service.localeCompare(b.service))
                .map(service =>
                  renderUnhealthyServiceRow(service)
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesStatus;
